import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import PageHeader from '@/components/feature/PageHeader';

const services = [
  {
    icon: 'ri-newspaper-line',
    title: '新闻资讯聚合',
    desc: '汇聚社会、科技、财经等领域的新闻与热点，及时呈现重要信息。',
  },
  {
    icon: 'ri-movie-2-line',
    title: '影视娱乐推荐',
    desc: '精选电影、电视剧与明星动态，用海报与预告帮你快速了解好内容。',
  },
  {
    icon: 'ri-store-2-line',
    title: '生活服务指南',
    desc: '健康、美食、旅游、购物等实用内容，让日常生活的选择更省心。',
  },
  {
    icon: 'ri-search-2-line',
    title: '全站智能搜索',
    desc: '一键检索全站内容，支持按类别筛选，快速定位你关心的信息。',
  },
];

const contacts = [
  { icon: 'ri-mail-line', label: '合作邮箱', value: 'hello@xiao-portal.example.com' },
  { icon: 'ri-customer-service-2-line', label: '内容反馈', value: 'support@xiao-portal.example.com' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '示例市 · 中央区 · 内容大厦 18F（演示信息）' },
];

export default function About() {
  useDocumentTitle(
    '小 - 关于我们 - 多元内容聚合平台',
    '了解「小」的平台定位、内容范围与联系方式，我们致力于打造一站式内容获取平台。',
    '关于小,平台介绍,联系方式,内容聚合'
  );

  return (
    <>
      <PageHeader
        title="关于「小」"
        description="一个以「小」为品牌的综合内容门户，专注于多元内容的聚合与呈现。"
        crumbs={[{ label: '首页', to: '/' }, { label: '关于' }]}
        icon="ri-information-line"
      />

      <div className="mx-auto w-full max-w-[1000px] px-4 py-10 md:px-6 md:py-14">
        <section className="mb-12">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">平台简介</h2>
          <div className="space-y-4 text-base leading-loose text-foreground-800">
            <p>
              「小」是一个以多元内容聚合为核心的综合门户网站，内容覆盖新闻资讯、影视娱乐与生活服务三大板块，面向追求信息全面、获取及时的普通网民。
            </p>
            <p>
              我们希望用清晰的分类、便捷的搜索和精选的推荐，把分散在各处的内容整理成一份易读、好找、耐看的信息清单，让用户在一个平台里就能完成日常的信息获取。
            </p>
            <p>
              本站为前端演示站点，所有内容均为展示用示例数据，不提供真实的新闻采编、影视播放或交易服务。
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">平台服务说明</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {services.map((s) => (
              <div
                key={s.title}
                className="rounded-lg border border-background-200/70 bg-background-50 p-5"
              >
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <h3 className="mb-2 font-heading text-base font-semibold text-foreground-950">{s.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
          <div className="flex flex-col gap-3">
            {contacts.map((c) => (
              <div
                key={c.label}
                className="flex flex-col gap-2 rounded-lg border border-background-200/70 bg-background-50 px-5 py-4 sm:flex-row sm:items-center sm:gap-5"
              >
                <span className="flex items-center gap-2 sm:w-40 sm:flex-shrink-0 text-sm font-medium text-foreground-800">
                  <i className={`${c.icon} text-lg text-primary-600`}></i>
                  {c.label}
                </span>
                <span className="text-sm text-foreground-600">{c.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-sm text-foreground-600">关注我们：</span>
            <span className="flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900">
              <i className="ri-wechat-line"></i>微信公众号：小内容聚合
            </span>
            <span className="flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900">
              <i className="ri-weibo-line"></i>微博：@小多元内容
            </span>
          </div>
        </section>

        <section className="rounded-lg bg-primary-900 p-8 text-background-100 md:p-10">
          <h2 className="mb-3 font-heading text-xl font-bold text-background-50 md:text-2xl">开始探索「小」</h2>
          <p className="mb-6 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            从最新资讯到热门影视，再到实用的生活指南，现在就去看看今天有什么值得一读的内容。
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
            >
              <i className="ri-home-4-line"></i>返回首页
            </Link>
            <Link
              to="/search"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-search-line"></i>全站搜索
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}